import {useMemo} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import gigsApi from '@/api/gigs';
import {PaginationParams, PaginationResponse} from '@/api/pagination';
import {InvitedGigsResponse} from '@/api/gigs/types';
import paginationUtils from '@/hooks/queries/common/pagination';
import {QUERY_STALE_TIME, getGigsQueryKey} from '../common';
import mapInvitedGigDtoToInvitedGig from './dataMapper';

const PREVIEW_FETCH_PAGE_SIZE = 3;

export interface UseInvitedGigsQueryOptions {
  enabled?: boolean;
  previewFetch?: boolean;
  include?: string[];
}

/**
 * Hook for fetching a list of invited gigs with filtering and pagination.
 * Uses infinite query to support loading more data as user scrolls.
 * If previewFetch is true, the query will fetch only 3 gigs (for suggested gigs page).
 */
export default function useInvitedGigsQuery(
  options: UseInvitedGigsQueryOptions = {}
) {
  const {enabled = true, previewFetch = false} = options;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<InvitedGigsResponse>({
    queryKey: getGigsQueryKey('invited', {previewFetch}, options.include),
    queryFn: async ({pageParam = 1}) => {
      if (previewFetch) {
        return gigsApi.invitedGigsList(
          {
            page: 1,
            per_page: PREVIEW_FETCH_PAGE_SIZE,
          },
          options.include
        );
      }
      const pagination: PaginationParams = {
        page: pageParam as number,
      };
      return gigsApi.invitedGigsList(pagination, options.include);
    },
    getNextPageParam: (lastPage) => {
      if (previewFetch) {
        return undefined;
      }
      return paginationUtils.getNextPageParam(lastPage);
    },
    enabled,
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIME,
  });

  const allGigs = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      page.data.map(mapInvitedGigDtoToInvitedGig)
    );
  }, [data?.pages]);

  const paginationInfo =
    data?.pages?.[data.pages.length - 1]?.pagination ||
    ({} as PaginationResponse);

  return {
    gigs: allGigs,
    pagination: paginationInfo,
    isLoading,
    isError,
    error,
    refetch,
    count: paginationInfo.totalEntries,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
